'use client';

import DynamicImage from '@/components/dynamic-image';
import { resolveImageAlt } from '@/lib/images/resolve-image-alt';
import { Carousel, CarouselContent, CarouselDots, CarouselItem } from '@/components/ui/carousel';
import type { BrandContent } from '@/lib/api/fetch-brand';
import { useMemo } from 'react';

export default function ProductTabsMark({ brand }: { brand: BrandContent }) {
    const brandName = brand.c_pimBrandName ?? '';
    const brandDescription = brand.c_pimBrandDescription ?? '';
    const brandClaim = brand.c_pimBrandClaim ?? '';

    const images = useMemo(() => {
        return [brand.c_image_1, brand.c_image_2, brand.c_image_3].filter((url): url is string => Boolean(url));
    }, [brand.c_image_1, brand.c_image_2, brand.c_image_3]);

    const imageKeys = useMemo(() => {
        const counts = new Map<string, number>();
        return images.map((src) => {
            const next = (counts.get(src) ?? 0) + 1;
            counts.set(src, next);
            return `${src}#${next}`;
        });
    }, [images]);

    return (
        <div className="space-y-6 w-full">
            <div className="flex justify-between max-lg:flex-col gap-4">
                <div className="flex flex-col gap-2 flex-1">
                    {brandName && <p className="label-l text-neutral-40">{brandName}</p>}
                    {brandClaim && <p className="heading-title-l text-neutral-10">{brandClaim}</p>}
                </div>
                {brandDescription && (
                    <div
                        className="prose prose-sm max-w-none marker:text-black [&_ul]:list-disc [&_ul]:pl-5 flex-1"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: brandDescription }}
                    />
                )}
            </div>
            {images.length > 0 && (
                <>
                    <div className="lg:hidden">
                        <Carousel
                            className="w-full"
                            opts={{ align: 'center', containScroll: 'trimSnaps' }}
                            aria-label={brandName || 'Brand images'}>
                            <div className="px-6">
                                <CarouselContent className="-ml-3">
                                    {images.map((src, index) => (
                                        <CarouselItem key={imageKeys[index]} className="basis-[66%] pl-3">
                                            <DynamicImage
                                                src={src}
                                                alt={resolveImageAlt({ name: brandName })}
                                                className="w-full overflow-hidden rounded-md object-cover aspect-[4/5]"
                                                loading="lazy"
                                                widths={{ base: '66vw' }}
                                            />
                                        </CarouselItem>
                                    ))}
                                </CarouselContent>
                            </div>
                            <CarouselDots
                                className="mt-6 justify-start gap-4 px-6"
                                activeDotClassName="bg-[var(--neutral-10)]"
                                inactiveDotClassName="bg-transparent border border-[var(--neutral-10)]"
                                getDotAriaLabel={(index) => `${brandName || 'Brand image'} ${index + 1}`}
                            />
                        </Carousel>
                    </div>
                    <div className="hidden lg:flex justify-between gap-20">
                        <div className="flex flex-col items-end gap-12 flex-1">
                            <DynamicImage
                                src={images[0]}
                                alt={resolveImageAlt({ name: brandName })}
                                className="w-[355px] overflow-hidden rounded-md object-cover aspect-[4/5]"
                                loading="lazy"
                                widths={[355]}
                            />
                            {images[1] && (
                                <DynamicImage
                                    src={images[1]}
                                    alt={resolveImageAlt({ name: brandName })}
                                    className="w-full overflow-hidden rounded-md object-cover aspect-[3/2]"
                                    loading="lazy"
                                    widths={[590]}
                                />
                            )}
                        </div>
                        {images[2] && (
                            <div className="flex items-center justify-center gap-12 flex-1">
                                <DynamicImage
                                    src={images[2]}
                                    alt={resolveImageAlt({ name: brandName })}
                                    className="w-[517px] overflow-hidden rounded-md object-cover aspect-[4/5]"
                                    loading="lazy"
                                    widths={[517]}
                                />
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
